/**
 * Makes the price facet agree with the currency the shopper picked.
 *
 * The facet is server-rendered from `cart.currency` (the shop currency) and Shopify reads
 * the `filter.v.price.gte`/`lte` params back in that same currency. The Nova / baCurr app
 * only rewrites prices client-side, so a shopper browsing in EUR sees "€" product prices
 * but a "$" filter that silently filters USD amounts.
 *
 * This translates the facet in both directions:
 *   - the symbol, bounds and any active values are converted shop -> display
 *   - on submit, what the shopper typed is converted display -> shop before it hits the URL
 *
 * The conversion runs through the app's own rates, so the filter and the prices always
 * use the same numbers. Amounts are rounded on the way back, so a value typed exactly on
 * a product's price can land a rounding unit off.
 *
 * The facet follows the app rather than leading it: until the shopper picks a currency the
 * app renders shop-currency prices, and the facet is left alone to match.
 *
 * Requires currency-selection-state.js. Remove both once the store moves to Shopify
 * Markets, where `cart.currency` follows the shopper and the facet needs no help.
 */
(function () {
  var PRICE_PARAM = /^filter\.v\.price\.(gte|lte)$/;

  function state() {
    return window.DefactoCurrency;
  }

  function round(value) {
    return Math.round(Number(value));
  }

  // Values are re-translated whenever the currency changes, so conversion always starts
  // from the shop-currency amounts the server rendered rather than the last conversion.
  function snapshot(input) {
    if (input.dataset.shopMax === undefined) {
      input.dataset.shopMax = input.getAttribute('data-max') || '';
      input.dataset.shopPlaceholder = input.getAttribute('placeholder') || '';
      input.dataset.shopValue = input.value || '';
    }
  }

  function translateFacet(priceRange) {
    var currency = state();
    var from = currency.shop();
    var to = currency.display();
    if (priceRange.dataset.currencyApplied === to) return;

    priceRange.querySelectorAll('.field-currency').forEach(function (node) {
      node.textContent = currency.symbolFor(to);
    });

    priceRange.querySelectorAll('input').forEach(function (input) {
      snapshot(input);

      var max = input.dataset.shopMax;
      if (max !== '' && !isNaN(Number(max))) {
        input.setAttribute('data-max', round(currency.convert(Number(max), from, to)));
      }

      var placeholder = input.dataset.shopPlaceholder;
      if (placeholder !== '' && !isNaN(Number(placeholder))) {
        input.setAttribute('placeholder', round(currency.convert(Number(placeholder), from, to)));
      }

      var value = input.dataset.shopValue;
      if (value !== '' && !isNaN(Number(value))) {
        input.value = round(currency.convert(Number(value), from, to));
      }
    });

    priceRange.dataset.currencyApplied = to;
  }

  // "The highest price is $569.00" sits outside <price-range> and is rendered with the
  // `money` filter, so it keeps the shop currency too. `.facets__selected` is also the
  // value-count element on every other facet ("(0)"), so only the one inside the price
  // facet's own <details> may be touched.
  function translateMaxPriceLabel(priceRange) {
    var currency = state();
    var from = currency.shop();
    var to = currency.display();

    var details = priceRange.closest('details');
    if (!details) return;

    details.querySelectorAll('.facets__selected').forEach(function (node) {
      if (node.dataset.currencyApplied === to) return;
      if (node.dataset.shopLabel === undefined) node.dataset.shopLabel = node.textContent;

      // Matches the formatted price as a whole, e.g. "$569.00".
      var original = node.dataset.shopLabel;
      var match = original.match(/[^\s]*\d[\d.,]*/);
      if (!match) return;
      var amount = Number(match[0].replace(/[^\d.]/g, ''));
      if (isNaN(amount)) return;

      node.textContent =
        from === to
          ? original
          : original.replace(match[0], currency.symbolFor(to) + round(currency.convert(amount, from, to)).toLocaleString());
      node.dataset.currencyApplied = to;
    });
  }

  function translateAll() {
    if (!state() || !state().canConvert()) return false;
    document.querySelectorAll('price-range').forEach(function (priceRange) {
      translateFacet(priceRange);
      translateMaxPriceLabel(priceRange);
    });
    return true;
  }

  // Convert what the shopper typed back to the shop currency, which is what Shopify filters on.
  function patchSearchParams() {
    var FacetFiltersForm = window.customElements.get('facet-filters-form');
    if (!FacetFiltersForm || FacetFiltersForm.prototype.createSearchParams.currencyPatched) return false;

    var original = FacetFiltersForm.prototype.createSearchParams;

    function createSearchParams(form) {
      var params = new URLSearchParams(original.call(this, form));
      var currency = state();
      var from = currency.display();
      var to = currency.shop();

      if (from !== to) {
        Array.from(params.keys())
          .filter(function (key) {
            return PRICE_PARAM.test(key);
          })
          .forEach(function (key) {
            var value = params.get(key);
            if (value === '' || isNaN(Number(value))) return;
            params.set(key, round(currency.convert(Number(value), from, to)));
          });
      }

      return params.toString();
    }

    createSearchParams.currencyPatched = true;
    FacetFiltersForm.prototype.createSearchParams = createSearchParams;
    return true;
  }

  // The facets are replaced wholesale on every filter change, so re-translate on mutation.
  function watchForRerenders() {
    var observer = new MutationObserver(function () {
      translateAll();
    });
    document
      .querySelectorAll('#main-collection-filters, #FacetFiltersForm, #FacetFiltersFormMobile')
      .forEach(function (node) {
        observer.observe(node, { childList: true, subtree: true });
      });
  }

  function init() {
    if (!state() || !window.customElements.get('facet-filters-form')) return false;
    if (!translateAll()) return false;

    patchSearchParams();
    watchForRerenders();
    state().onChange(translateAll);
    return true;
  }

  // `baCurr` and the facet element both arrive asynchronously.
  if (!init()) {
    var attempts = 0;
    var timer = setInterval(function () {
      attempts += 1;
      if (init() || attempts > 40) clearInterval(timer);
    }, 250);
  }
})();
